import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "./authContext";
import { ACTIVE_PORTAL_KEY } from "../Services/sessionStorage";

export const PortalContext = createContext();

// Short display noun per portal — shared by the sidebar's account label and
// the "dynamic island" role-switch indicator so both read from one source.
export const PORTAL_DISPLAY_NAMES = {
  admin: "Admin",
  therapist: "Therapist",
  patient: "Patient",
};

// 'admin' bucket covers both the admin and operator roles — they already
// share the same dashboard content today, differentiated only by isAdmin
// checks inside it (e.g. admin-only nav items, Settings dialog).
function computeAvailablePortals(roles) {
  const portals = [];
  if (roles.includes("admin") || roles.includes("operator")) portals.push("admin");
  if (roles.includes("therapist")) portals.push("therapist");
  if (roles.includes("patient")) portals.push("patient");
  return portals;
}

function defaultPortal(availablePortals) {
  if (availablePortals.includes("admin")) return "admin";
  if (availablePortals.includes("therapist")) return "therapist";
  if (availablePortals.includes("patient")) return "patient";
  return null;
}

export const PortalProvider = ({ children }) => {
  const { roles } = useAuth();
  const availablePortals = useMemo(() => computeAvailablePortals(roles), [roles]);

  const [activePortal, setActivePortal] = useState(() => {
    const stored = localStorage.getItem(ACTIVE_PORTAL_KEY);
    return stored && availablePortals.includes(stored) ? stored : defaultPortal(availablePortals);
  });

  // If roles change (login/logout/account switch) and the stored/active
  // portal is no longer valid for this account, fall back to the default.
  useEffect(() => {
    if (activePortal && availablePortals.includes(activePortal)) return;
    setActivePortal(defaultPortal(availablePortals));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePortals]);

  // Keep the active portal in storage even when it's just the default (not
  // only after a manual switch) — apiFetch sends it as X-Portal so the
  // backend's usage log knows which portal a multi-role user was in.
  useEffect(() => {
    if (activePortal) localStorage.setItem(ACTIVE_PORTAL_KEY, activePortal);
  }, [activePortal]);

  const switchPortal = useCallback(
    (portal) => {
      if (!availablePortals.includes(portal)) return;
      setActivePortal(portal);
      localStorage.setItem(ACTIVE_PORTAL_KEY, portal);
    },
    [availablePortals]
  );

  return (
    <PortalContext.Provider value={{ activePortal, availablePortals, switchPortal }}>
      {children}
    </PortalContext.Provider>
  );
};

export const usePortal = () => useContext(PortalContext);
